import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { uniq as _uniq, isEqual as _isEqual } from 'underscore';
import type { RootState } from '../../../../store/models';
import { stripPhotoSuffix } from '../../../../modules/formatters/formatProfilePhoto';
import {
  IProviderService,
  ISearchResultProviderData,
  RenderImageData,
} from '../../../../api/search';

export default (providerId, galleryImages = []) => {
  const [searchImages, setSearchImages] = useState<RenderImageData[]>([]);
  const [images, setImages] = useState(galleryImages);
  const searchResults = useSelector<RootState, ISearchResultProviderData[]>(
    state => state.search.results,
  );

  // Find all the images from the most recent search for this provider and add a service ID to each
  useEffect(() => {
    // eslint-disable-next-line eqeqeq
    const providerResult = searchResults.find(p => p.id == providerId);
    if (!providerResult) {
      if (searchImages.length) {
        setSearchImages([]);
      }
      return;
    }

    const {
      matched_services: matchedServices,
    } = providerResult;
    const imageData = matchedServices.reduce((
      result: RenderImageData[],
      service: IProviderService,
    ) => {
      const {
        clip_images: clipImages = [],
        num_service_images: numImages = 0,
      } = service;
      const searchImagesForService = clipImages.slice(0, numImages).map(i => ({
        ...i,
        service_id: service.id,
      }));
      return [
        ...result,
        ...searchImagesForService,
      ];
    }, [] as RenderImageData[]);

    if (!_isEqual(imageData, searchImages)) {
      setSearchImages(imageData);
    }
  }, [
    searchResults,
    providerId,
    searchImages,
  ]);

  // Combine gallery results with search images, dedupe, and format
  useEffect(() => {
    let newImages = galleryImages;
    if (searchImages && searchImages.length) {
      newImages = _uniq([
        ...searchImages.map(i => ({
          id: i.image_id,
          url: stripPhotoSuffix(i.image_url),
          ...i,
        })),
        ...galleryImages,
      ], false, 'id');
    }
    if (!_isEqual(newImages, images)) {
      setImages(newImages);
    }
  }, [
    searchImages,
    galleryImages,
    images,
  ]);

  return [images];
};
